// core
import React, { useMemo } from "react";

// hooks
import useQuoteGroupState from "../hooks/useQuoteGroupState";

// enums
import { Gender } from "../../common/interfaces/enums/Gender";
import { PaymentFrequency } from "../../common/interfaces/enums/PaymentFrequency";
import { ProductType } from "../../common/interfaces/enums/ProductType";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import { SortBy } from "../enums/SortBy";
import { PremiumStructure } from "../../common/interfaces/enums/PremiumStructure";
import { QuoteTableView } from "../enums/QuoteTableView";

export const QuoteGroupContext = React.createContext<
    ReturnType<typeof useQuoteGroupState>
>(undefined);

const RootContextProvider: React.FC = (props) => {
    const quoteGroupState = useQuoteGroupState({
        groupId: "",
        isAdmin: window.dataLayer[0]?.AdminUser?.toLowerCase() === "true",
        isFromCrm: false,
        clients: [
            {
                personId: 0,
                gender: Gender.Male,
                age: 35,
                smokerStatus: SmokerStatus.NonSmoker,
                isChild: false,
                isActive: true
            }
        ],
        quoteConfigs: {
            life: {
                isEnabled: false,
                config: {
                    caseId: 0,
                    sortBy: SortBy.LowestPrice,
                    paymentFrequency: PaymentFrequency.Fortnightly,
                    productType: ProductType.Life,
                    clients: [],
                    addons: [],
                    allowChildren: false,
                    premiumStructure: PremiumStructure.Stepped,
                    isLatestVersion: false,
                    versionId: 0
                }
            },
            health: {
                isEnabled: false,
                config: {
                    caseId: 0,
                    sortBy: SortBy.LowestPrice,
                    paymentFrequency: PaymentFrequency.Fortnightly,
                    productType: ProductType.Health,
                    clients: [],
                    addons: [],
                    allowChildren: true,
                    isLatestVersion: false,
                    versionId: 0
                }
            },
            trauma: {
                isEnabled: false,
                config: {
                    caseId: 0,
                    sortBy: SortBy.LowestPrice,
                    paymentFrequency: PaymentFrequency.Fortnightly,
                    productType: ProductType.Trauma,
                    clients: [],
                    addons: [],
                    allowChildren: false,
                    isLatestVersion: false,
                    versionId: 0
                }
            },
            mortgage: {
                isEnabled: false,
                config: {
                    caseId: 0,
                    sortBy: SortBy.LowestPrice,
                    paymentFrequency: PaymentFrequency.Fortnightly,
                    productType: ProductType.Mortgage,
                    repaymentAmount: 0,
                    mortgageAmount: 0,
                    repaymentFrequency: PaymentFrequency.Fortnightly,
                    clients: [],
                    addons: [],
                    allowChildren: false,
                    isLatestVersion: false,
                    versionId: 0
                }
            },
            income: {
                isEnabled: false,
                config: {
                    caseId: 0,
                    sortBy: SortBy.LowestPrice,
                    paymentFrequency: PaymentFrequency.Fortnightly,
                    productType: ProductType.Income,
                    clients: [],
                    addons: [],
                    allowChildren: false,
                    isLatestVersion: false,
                    versionId: 0
                }
            }
        },
        quoteResults: {
            isLoading: false,
            isValid: false,
            hasCreatedQuotes: false,
            paymentFrequency: PaymentFrequency.Fortnightly,
            cards: []
        },
        contactDetails: {
            hasContactDetails: false,
            name: "",
            emailAddress: "",
            phoneNumber: ""
        },

        currentView: QuoteTableView.Overview,
        isStreamlineFlow: false
    });

    const data = useMemo(() => ({ ...quoteGroupState }), [quoteGroupState]);

    return (
        <QuoteGroupContext.Provider value={data}>
            {props.children}
        </QuoteGroupContext.Provider>
    );
};

export default RootContextProvider;
